// src/components/Refunds.js
// Remboursements — /refunds. KPI strip with the money rendered as what it
// is: a composition (transport / contenu / assurance segments of the total),
// currency taken honestly from stats.by_currency (multi-currency data scopes
// the money KPIs to the dominant currency and says so). Breakdowns as
// proportion rows. Filters (debounced for text) + page live in the URL.
// Table row click → /refunds/:id — the raw Mongo _id column is gone.
// Shares Refunds.css with TicketRefunds (frozen) and RefundDetail: new
// classes are prefixed rf-, legacy refund-* classes those files use remain.
// KI-158: refunds are created from here (« Nouveau remboursement ») and the
// AI agent records carrier decisions; money KPIs count what was GRANTED
// (approved / processing / paid), a request or a refusal is not refunded money.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaExternalLinkAlt, FaSync, FaChevronRight, FaPlus, FaRobot } from 'react-icons/fa';
import RefundCreateModal from './RefundCreateModal';
import {
  REFUND_REASONS,
  REFUND_SOURCES,
  REFUND_STATUSES,
  REFUND_STATUS_DOT as STATUS_DOT,
  refundAmountPending,
} from '../utils/refunds';
import './Refunds.css';

const MONGO_ID_RE = /^[a-f0-9]{24}$/i;

const Refunds = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL (Back from a refund detail restores everything)
  const filters = {
    status: searchParams.get('status') || '',
    source: searchParams.get('source') || '',
    reason: searchParams.get('reason') || '',
    carrier: searchParams.get('carrier') || '',
    client: searchParams.get('client') || '',
    start_date: searchParams.get('start') || '',
    end_date: searchParams.get('end') || '',
    ticket_id: searchParams.get('ticket') || '',
    order: searchParams.get('order') || '',
    recorded_by: searchParams.get('by') || '',
  };
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Debounced text filters — the old page fired two API calls per keystroke
  const [carrierInput, setCarrierInput] = useState(filters.carrier);
  const [clientInput, setClientInput] = useState(filters.client);
  const [ticketInput, setTicketInput] = useState(filters.ticket_id);
  const [orderInput, setOrderInput] = useState(filters.order);
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const updates = {};
      if (carrierInput.trim() !== filters.carrier) updates.carrier = carrierInput.trim();
      if (clientInput.trim() !== filters.client) updates.client = clientInput.trim();
      if (orderInput.trim() !== filters.order) updates.order = orderInput.trim();
      const ticketValue = MONGO_ID_RE.test(ticketInput.trim()) ? ticketInput.trim() : '';
      if (ticketValue !== filters.ticket_id) updates.ticket = ticketValue;
      if (Object.keys(updates).length > 0) updateParams({ ...updates, page: null });
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierInput, clientInput, ticketInput, orderInput]);

  const [items, setItems] = useState(null); // null = loading
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` }), []);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.source) params.append('source', filters.source);
    if (filters.reason) params.append('reason', filters.reason);
    if (filters.carrier) params.append('carrier', filters.carrier);
    if (filters.client) params.append('client', filters.client);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.ticket_id) params.append('ticket_id', filters.ticket_id);
    if (filters.order) params.append('order', filters.order);
    if (filters.recorded_by) params.append('recorded_by', filters.recorded_by);
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchRefunds = useCallback(async () => {
    setError('');
    try {
      const params = buildFilterParams();
      params.append('page', String(page));
      params.append('limit', '25');
      const response = await axios.get(`/api/logistics/refunds?${params.toString()}`, { headers });
      const data = response.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalPages(Number(data.total_pages || 1));
      setTotalItems(Number.isFinite(Number(data.total)) ? Number(data.total) : null);
    } catch (err) {
      console.error('Failed to fetch refunds:', err);
      setError(err?.response?.data?.detail || t('refunds.fetchError', 'Impossible de charger les remboursements.'));
      setItems([]);
    }
  }, [buildFilterParams, headers, page, t]);

  const fetchRefundStats = useCallback(async () => {
    try {
      const response = await axios.get(`/api/logistics/refunds/stats?${buildFilterParams().toString()}`, { headers });
      setStats(response.data || null);
    } catch (err) {
      console.error('Failed to fetch refund stats:', err);
      setStats(null);
    }
  }, [buildFilterParams, headers]);

  useEffect(() => { fetchRefunds(); }, [fetchRefunds]);
  useEffect(() => { fetchRefundStats(); }, [fetchRefundStats]);

  // ------------------------------------------------------------ formatting

  const fmtMoney = (amount, currency) =>
    `${Number(amount || 0).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

  const fmtDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Currency honesty: aggregates come from by_currency. One currency → its
  // bucket (= summary). Several → the dominant bucket, and we say so.
  const summary = stats?.summary || null;
  const currencyEntries = Object.entries(stats?.by_currency || {}).sort((a, b) => (b[1].count || 0) - (a[1].count || 0));
  const multiCurrency = currencyEntries.length > 1;
  const currency = currencyEntries[0]?.[0] || 'EUR';
  const moneyStats = multiCurrency ? currencyEntries[0][1] : summary;
  const moneyScopeNote = multiCurrency
    ? t('refunds.currencyScope', '{{currency}} uniquement — {{others}} autres devises exclues', {
        currency, others: currencyEntries.length - 1,
      })
    : null;

  // Granted = approved / processing / paid: a request or a refusal is not money.
  const granted = moneyStats?.granted_by_field || {};
  const compTotal = Number(moneyStats?.granted_refund_amount || 0);
  const paidTotal = Number(moneyStats?.paid_refund_amount || 0);
  const amountUnknown = Number(summary?.amount_unknown_count || 0);
  const compParts = moneyStats ? [
    { key: 'shipping', label: t('refunds.shippingAmount', 'Transport'), value: Number(granted.shipping_cost_refund_amount || 0), cls: 'shipping' },
    { key: 'content', label: t('refunds.contentAmount', 'Contenu'), value: Number(granted.content_refund_amount || 0), cls: 'content' },
    { key: 'insurance', label: t('refunds.insuranceAmount', 'Assurance'), value: Number(granted.insurance_refund_amount || 0), cls: 'insurance' },
  ].filter((p) => p.value > 0) : [];
  const compKnown = compParts.reduce((sum, p) => sum + p.value, 0);
  const compOther = Math.max(0, compTotal - compKnown);
  if (compTotal > 0 && compOther / compTotal > 0.01) {
    compParts.push({ key: 'other', label: t('refunds.otherAmount', 'Autre'), value: compOther, cls: 'other' });
  }

  const topEntries = (breakdown = {}) =>
    Object.entries(breakdown)
      .sort(([, a], [, b]) => Number(b.granted_refund_amount || 0) - Number(a.granted_refund_amount || 0) || (b.count || 0) - (a.count || 0))
      .slice(0, 6);
  const topCarriers = topEntries(stats?.by_carrier);
  const topClients = topEntries(stats?.by_client);
  const maxBreakdownAmount = Math.max(1, ...[...topCarriers, ...topClients].map(([, b]) => Number(b.granted_refund_amount || 0)));

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const response = await axios.get(`/api/logistics/refunds/export?${buildFilterParams().toString()}`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'refunds.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export refunds:', err);
      setError(err?.response?.data?.detail || t('refunds.exportError', 'Échec de l’export CSV.'));
    } finally {
      setExporting(false);
    }
  };

  const getPageItems = () => {
    const anchors = new Set([1, totalPages, page - 1, page, page + 1]);
    const pages = [...anchors].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    pages.forEach((p) => {
      if (p - prev > 1) out.push(`gap-${p}`);
      out.push(p);
      prev = p;
    });
    return out;
  };

  const loading = items === null;
  const hasFilters = Object.values(filters).some(Boolean);
  const ticketInputInvalid = ticketInput.trim() !== '' && !MONGO_ID_RE.test(ticketInput.trim());

  return (
    <div className="refunds-list-page">
      {/* Header */}
      <div className="rf-header">
        <div className="rf-heading">
          <h1>{t('refunds.title', 'Remboursements')}</h1>
          <p>{t('refunds.subtitle', 'Compensations créées depuis les tickets support.')}</p>
        </div>
        <div className="rf-header-actions">
          <button
            type="button"
            className="rf-icon-btn"
            onClick={() => { fetchRefunds(); fetchRefundStats(); }}
            title={t('refunds.refresh', 'Actualiser')}
            aria-label={t('refunds.refresh', 'Actualiser')}
          >
            <FaSync />
          </button>
          <button type="button" className="u-btn u-btn--secondary" onClick={handleExport} disabled={exporting}>
            <FaDownload /> {exporting ? t('refunds.exporting', 'Export…') : t('refunds.exportCSV', 'Exporter CSV')}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={() => setCreating(true)}>
            <FaPlus /> {t('refunds.newRefund', 'Nouveau remboursement')}
          </button>
        </div>
      </div>

      {creating && (
        <RefundCreateModal
          onClose={() => setCreating(false)}
          onCreated={(refund) => { setCreating(false); navigate(`/refunds/${refund._id}`); }}
        />
      )}

      {error && <div className="rf-error" role="alert">{error}</div>}

      {/* KPI strip — money as a composition */}
      {summary && summary.count > 0 && (
        <div className="rf-kpi-strip">
          <div className="rf-kpi">
            <span className="rf-kpi-value">{Number(summary.count).toLocaleString(lang)}</span>
            <span className="rf-kpi-label">{t('refunds.kpiCount', 'remboursements')}</span>
            {Number(summary.ai_recorded_count) > 0 && (
              <span className="rf-kpi-sub">
                {t('refunds.kpiAiRecorded', 'dont {{count}} enregistrés par l’IA', { count: Number(summary.ai_recorded_count).toLocaleString(lang) })}
              </span>
            )}
            {Number(summary.rejected_count) > 0 && (
              <span className="rf-kpi-sub">
                {t('refunds.kpiRejected', '{{count}} refusés', { count: Number(summary.rejected_count).toLocaleString(lang) })}
              </span>
            )}
          </div>
          <div className="rf-kpi rf-kpi-hero">
            <span className="rf-kpi-value">{fmtMoney(compTotal, currency)}</span>
            <span className="rf-kpi-label">
              {t('refunds.kpiGranted', 'montant accordé')}
              {moneyScopeNote && <span className="rf-kpi-scope"> · {moneyScopeNote}</span>}
            </span>
            {(paidTotal > 0 || amountUnknown > 0) && (
              <span className="rf-kpi-sub">
                {[paidTotal > 0 && t('refunds.kpiPaid', 'dont {{amount}} payés', { amount: fmtMoney(paidTotal, currency) }),
                  amountUnknown > 0 && t('refunds.kpiAmountUnknown', '{{count}} sans montant pour l’instant', { count: amountUnknown })]
                  .filter(Boolean).join(' · ')}
              </span>
            )}
            {compTotal > 0 && compParts.length > 1 && (
              <div className="rf-comp">
                <div className="rf-comp-bar" role="img" aria-label={t('refunds.compositionAria', 'Répartition du montant remboursé')}>
                  {compParts.map((part) => (
                    <span key={part.key} className={`rf-comp-seg ${part.cls}`} style={{ width: `${(part.value / compTotal) * 100}%` }} />
                  ))}
                </div>
                <div className="rf-comp-legend">
                  {compParts.map((part) => (
                    <span key={part.key} className={`rf-comp-item ${part.cls}`}>
                      {part.label} · {fmtMoney(part.value, currency)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="rf-kpi">
            <span className="rf-kpi-value">{fmtMoney(moneyStats?.average_refund_amount || 0, currency)}</span>
            <span className="rf-kpi-label">{t('refunds.kpiAverage', 'remboursement moyen')}</span>
            {Number(summary.insured_refunds_count) > 0 && (
              <span className="rf-kpi-sub">
                {t('refunds.kpiInsured', 'dont {{total}} assurés', { total: Number(summary.insured_refunds_count).toLocaleString(lang) })}
              </span>
            )}
          </div>
          {Number(moneyStats?.insurance_price || 0) > 0 && (
            <div className="rf-kpi">
              <span className="rf-kpi-value">{fmtMoney(moneyStats.insurance_price, currency)}</span>
              <span className="rf-kpi-label">{t('refunds.kpiInsurancePrice', 'primes d’assurance payées')}</span>
              <span className="rf-kpi-sub">{t('refunds.kpiInsurancePriceHint', 'coût, pas un remboursement')}</span>
            </div>
          )}
        </div>
      )}

      {/* Breakdowns */}
      {(topCarriers.length > 0 || topClients.length > 0) && (
        <div className="rf-breakdowns">
          {[{ title: t('refunds.byCarrier', 'Par transporteur'), rows: topCarriers },
            { title: t('refunds.byClient', 'Par client'), rows: topClients }]
            .filter((g) => g.rows.length > 0)
            .map((group) => (
              <div key={group.title} className="rf-breakdown-card">
                <h3>{group.title}</h3>
                {group.rows.map(([label, bucket]) => (
                  <div key={label} className="rf-breakdown-row">
                    <span className="rf-breakdown-label" title={label}>{label}</span>
                    <span className="rf-breakdown-amount">{fmtMoney(bucket.granted_refund_amount, currency)}</span>
                    <span className="rf-breakdown-bar">
                      <span className="rf-breakdown-fill" style={{ width: `${(Number(bucket.granted_refund_amount || 0) / maxBreakdownAmount) * 100}%` }} />
                    </span>
                    <span className="rf-breakdown-count">{bucket.count}</span>
                  </div>
                ))}
              </div>
            ))}
        </div>
      )}

      {/* List card */}
      <div className="rf-card">
        <div className="rf-toolbar">
          <label className="rf-toolbar-field">
            <span>{t('refunds.status', 'Statut')}</span>
            <select value={filters.status} onChange={(e) => updateParams({ status: e.target.value, page: null })}>
              <option value="">{t('refunds.allStatuses', 'Tous')}</option>
              {REFUND_STATUSES.map((status) => (
                <option key={status} value={status}>{t(`refunds.statuses.${status}`, status)}</option>
              ))}
            </select>
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.source', 'Source')}</span>
            <select value={filters.source} onChange={(e) => updateParams({ source: e.target.value, page: null })}>
              <option value="">{t('refunds.allSources', 'Toutes')}</option>
              {REFUND_SOURCES.map((source) => (
                <option key={source} value={source}>{t(`refunds.sources.${source}`, source)}</option>
              ))}
            </select>
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.reason', 'Motif')}</span>
            <select value={filters.reason} onChange={(e) => updateParams({ reason: e.target.value, page: null })}>
              <option value="">{t('refunds.allReasons', 'Tous')}</option>
              {REFUND_REASONS.map((reason) => (
                <option key={reason} value={reason}>{t(`refunds.reasons.${reason}`, reason)}</option>
              ))}
            </select>
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.carrierName', 'Transporteur')}</span>
            <input
              type="text"
              value={carrierInput}
              onChange={(e) => setCarrierInput(e.target.value)}
              placeholder={t('refunds.carrierPlaceholder', 'Nom…')}
            />
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.clientName', 'Client')}</span>
            <input
              type="text"
              value={clientInput}
              onChange={(e) => setClientInput(e.target.value)}
              placeholder={t('refunds.clientPlaceholder', 'Client ou marchand…')}
            />
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.orderReference', 'Commande')}</span>
            <input
              type="text"
              value={orderInput}
              onChange={(e) => setOrderInput(e.target.value)}
              placeholder={t('refunds.orderPlaceholder', 'Référence de commande')}
            />
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.recordedBy', 'Enregistré par')}</span>
            <select value={filters.recorded_by} onChange={(e) => updateParams({ by: e.target.value, page: null })}>
              <option value="">{t('refunds.recordedByAll', 'Tous')}</option>
              <option value="ai">{t('refunds.recordedByAiOption', 'Agent IA')}</option>
              <option value="human">{t('refunds.recordedByTeam', 'Équipe')}</option>
            </select>
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.startDate', 'Du')}</span>
            <input type="date" value={filters.start_date} onChange={(e) => updateParams({ start: e.target.value, page: null })} />
          </label>
          <label className="rf-toolbar-field">
            <span>{t('refunds.endDate', 'Au')}</span>
            <input type="date" value={filters.end_date} onChange={(e) => updateParams({ end: e.target.value, page: null })} />
          </label>
          <label className={`rf-toolbar-field ${ticketInputInvalid ? 'invalid' : ''}`}>
            <span>{t('refunds.ticketId', 'Ticket')}</span>
            <input
              type="text"
              value={ticketInput}
              onChange={(e) => setTicketInput(e.target.value)}
              placeholder={t('refunds.ticketIdPlaceholder', 'Identifiant de ticket')}
            />
            {ticketInputInvalid && (
              <em className="rf-field-hint">{t('refunds.ticketIdHint', 'Identifiant complet requis (24 caractères)')}</em>
            )}
          </label>
        </div>

        {loading ? (
          <div className="rf-skeleton" aria-hidden="true">
            {Array.from({ length: 6 }, (_, i) => <div key={i} className="rf-skeleton-row" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="rf-empty">
            {hasFilters ? (
              <p>{t('refunds.empty', 'Aucun remboursement ne correspond aux filtres.')}</p>
            ) : (
              <>
                <p>{t('refunds.emptyAll', 'Aucun remboursement enregistré pour l’instant.')}</p>
                <p className="rf-empty-hint">
                  {t('refunds.emptyAllHint', 'L’agent IA enregistre ici les décisions de remboursement des transporteurs dès leur réponse ; vous pouvez aussi en saisir un vous-même.')}
                </p>
                <button type="button" className="u-btn u-btn--primary" onClick={() => setCreating(true)}>
                  <FaPlus /> {t('refunds.newRefund', 'Nouveau remboursement')}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="rf-table-scroll">
            <table className="rf-table">
              <colgroup>
                <col style={{ width: '16%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '36px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('refunds.ticket', 'Ticket')}</th>
                  <th>{t('refunds.clientName', 'Client')}</th>
                  <th>{t('refunds.carrierName', 'Transporteur')}</th>
                  <th>{t('refunds.reason', 'Motif')}</th>
                  <th>{t('refunds.status', 'Statut')}</th>
                  <th className="rf-th-amount">{t('refunds.amount', 'Montant')}</th>
                  <th>{t('refunds.createdAt', 'Créé')}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {items.map((refund) => (
                  <tr
                    key={refund._id}
                    className="rf-row"
                    tabIndex={0}
                    onClick={() => navigate(`/refunds/${refund._id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/refunds/${refund._id}`); }}
                  >
                    <td>
                      {refund.ticket_id ? (
                        <button
                          type="button"
                          className="rf-ticket-link"
                          title={t('refunds.openTicket', 'Ouvrir le ticket')}
                          onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${refund.ticket_id}/messages`); }}
                        >
                          {refund.external_ticket_id || refund.ticket_id.slice(-8)}
                          <FaExternalLinkAlt aria-hidden="true" />
                        </button>
                      ) : (
                        <span className="rf-muted-dash">—</span>
                      )}
                    </td>
                    <td title={[refund.client_name || refund.merchant_name, refund.order_reference].filter(Boolean).join(' · ')}>
                      <span className="rf-cell-stack">
                        <span>{refund.client_name || refund.merchant_name || <span className="rf-muted-dash">—</span>}</span>
                        {refund.order_reference && (
                          <span className="rf-cell-sub">{t('refunds.orderShort', 'Cde {{order}}', { order: refund.order_reference })}</span>
                        )}
                      </span>
                    </td>
                    <td title={refund.carrier_name || ''}>
                      {refund.carrier_name || <span className="rf-muted-dash">—</span>}
                    </td>
                    <td>{refund.reason ? t(`refunds.reasons.${refund.reason}`, refund.reason) : <span className="rf-muted-dash">—</span>}</td>
                    <td>
                      <span className="rf-status">
                        <span className="rf-status-dot" style={{ background: STATUS_DOT[refund.status] || 'var(--blue)' }} aria-hidden="true" />
                        {t(`refunds.statuses.${refund.status}`, refund.status)}
                        {refund.created_by_kind === 'ai' && (
                          <span className="rf-ai-badge" title={t('refunds.recordedByAiTitle', 'Enregistré par l’agent IA depuis la réponse du transporteur')}>
                            <FaRobot aria-hidden="true" /> {t('refunds.recordedByAi', 'IA')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="rf-td-amount">
                      {refundAmountPending(refund)
                        ? <span className="rf-amount-pending">{t('refunds.amountToConfirm', 'Montant à confirmer')}</span>
                        : fmtMoney(refund.total_refund_amount, refund.currency || 'EUR')}
                    </td>
                    <td>{fmtDate(refund.created_at)}</td>
                    <td className="rf-td-chevron"><FaChevronRight aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="rf-card-footer">
            {totalItems !== null && (
              <span className="rf-footer-count">
                {t('refunds.matchCount', '{{total}} résultats', { total: totalItems.toLocaleString(lang) })}
              </span>
            )}
            <nav className="rf-pager" aria-label={t('refunds.pagination', 'Pagination')}>
              <button type="button" disabled={page <= 1} onClick={() => updateParams({ page: page - 1 <= 1 ? null : page - 1 })} aria-label={t('common.previous')}>‹</button>
              {getPageItems().map((item) => (
                typeof item === 'number' ? (
                  <button
                    key={item}
                    type="button"
                    className={item === page ? 'active' : ''}
                    aria-current={item === page ? 'page' : undefined}
                    onClick={() => updateParams({ page: item <= 1 ? null : item })}
                  >
                    {item}
                  </button>
                ) : (
                  <span key={item} className="rf-pager-ellipsis" aria-hidden="true">…</span>
                )
              ))}
              <button type="button" disabled={page >= totalPages} onClick={() => updateParams({ page: page + 1 })} aria-label={t('common.next')}>›</button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Refunds;
